import { useEffect } from "react";
import { capitalizeWords, TEXTUAL_INPUT_TYPES } from "./autoCap";

/**
 * useGlobalAutoCap — installs a single document-level listener pair that
 * auto-capitalises the first letter of every word inside EVERY text input
 * and textarea in the app, whether it's a shadcn `<Input>` wrapper or a
 * raw HTML `<input>` / `<textarea>` element.
 *
 * Bypass keys (already worked on the shadcn wrappers, now enforced globally):
 *   • Hold **Shift** while typing → that keystroke is skipped
 *   • Turn on **Caps Lock** → the browser is producing uppercase already, skip
 *
 * Skipped elements:
 *   • Any non-textual input type (email, password, number, url, tel, date, …)
 *   • Any input with `data-no-autocap` attribute (per-field opt-out)
 *   • `<input>` / `<textarea>` inside a `[contenteditable]` region
 *   • Elements with autocomplete="off" AND spellcheck="false"  ← purposely
 *     kept OFF this list — searchable pickers still get autoCap so behaviour
 *     is uniform. Opt-out with `data-no-autocap` if you need lowercase search.
 */
export function useGlobalAutoCap() {
  useEffect(() => {
    let shiftHeld = false;
    let capsLockOn = false;

    const trackModifiers = (e) => {
      shiftHeld = !!e.shiftKey;
      if (typeof e.getModifierState === "function") {
        capsLockOn = e.getModifierState("CapsLock");
      }
    };

    const handleInput = (e) => {
      const el = e.target;
      if (!el || (el.tagName !== "INPUT" && el.tagName !== "TEXTAREA")) return;
      if (el.hasAttribute("data-no-autocap")) return;
      if (el.tagName === "INPUT") {
        const t = (el.getAttribute("type") || "").toLowerCase();
        // Only auto-cap when the type is a plain-text one. Everything else
        // (email / password / number / url / tel / date / search / …) must
        // stay untouched so login credentials, GSTIN case, OTP, prices etc.
        // are never mangled.
        if (t && !TEXTUAL_INPUT_TYPES.has(t)) return;
      }
      // Skip when either bypass key is engaged.
      if (shiftHeld || capsLockOn) {
        shiftHeld = false;  // Shift is per-keystroke, reset now.
        return;
      }
      const original = el.value;
      const next = capitalizeWords(original);
      if (next === original) return;
      // Preserve caret position — length is identical so we can just
      // restore the previous selection.
      const start = el.selectionStart;
      const end = el.selectionEnd;
      // Use the native setter so React's synthetic onChange still sees the
      // update. This is the standard React-controlled-input dance.
      const proto = el.tagName === "TEXTAREA"
        ? window.HTMLTextAreaElement.prototype
        : window.HTMLInputElement.prototype;
      const setter = Object.getOwnPropertyDescriptor(proto, "value").set;
      setter.call(el, next);
      el.dispatchEvent(new Event("input", { bubbles: true }));
      try {
        el.setSelectionRange(start, end);
      } catch (_) {
        /* setSelectionRange throws on some input types (e.g. hidden) — ignore */
      }
    };

    document.addEventListener("keydown", trackModifiers, true);
    document.addEventListener("keyup", trackModifiers, true);
    document.addEventListener("input", handleInput, true);

    return () => {
      document.removeEventListener("keydown", trackModifiers, true);
      document.removeEventListener("keyup", trackModifiers, true);
      document.removeEventListener("input", handleInput, true);
    };
  }, []);
}
